import { API_CONFIG } from '@/config/api';
import { httpClient } from '@/utils/httpClient';

export interface PersonSizingAssignmentInput {
  catalogId: string;
  parentId: string;
}

export interface SetPersonSizingAssignmentsInput {
  personId: string;
  assignments: PersonSizingAssignmentInput[];
  validateOnly?: boolean;
}

export interface PersonSizingAssignmentResult {
  assignmentIndex: number;
  personSizingId: string | null;
  personId: string;
  catalogId: string;
  parentId: string;
  displayName: string;
}

interface SetPersonSizingAssignmentsRpcRow {
  assignment_index: number;
  person_sizing_id: string | null;
  person_id: string;
  catalog_id: string;
  parent_id: string;
  display_name: string;
}

const normalizeId = (value: string): string => value.trim();

export class PersonSizingService {
  private rpcEndpoint = `${API_CONFIG.ENDPOINTS.RPC}/set_person_sizing_assignments`;

  async setPersonSizingAssignments({
    personId,
    assignments,
    validateOnly = false,
  }: SetPersonSizingAssignmentsInput): Promise<PersonSizingAssignmentResult[]> {
    const payload = {
      p_person_id: normalizeId(personId),
      p_assignments: assignments.map((assignment) => ({
        catalog_id: normalizeId(assignment.catalogId),
        parent_id: normalizeId(assignment.parentId),
      })),
      p_validate_only: validateOnly,
    };

    const response = await httpClient.post<SetPersonSizingAssignmentsRpcRow[]>(
      this.rpcEndpoint,
      payload,
    );

    return response.map((row) => ({
      assignmentIndex: row.assignment_index,
      personSizingId: row.person_sizing_id,
      personId: row.person_id,
      catalogId: row.catalog_id,
      parentId: row.parent_id,
      displayName: row.display_name,
    }));
  }

  async validatePersonSizingAssignments(
    personId: string,
    assignments: PersonSizingAssignmentInput[],
  ): Promise<PersonSizingAssignmentResult[]> {
    return this.setPersonSizingAssignments({ personId, assignments, validateOnly: true });
  }

  async savePersonSizingAssignments(
    personId: string,
    assignments: PersonSizingAssignmentInput[],
  ): Promise<PersonSizingAssignmentResult[]> {
    return this.setPersonSizingAssignments({ personId, assignments, validateOnly: false });
  }
}

export const personSizingService = new PersonSizingService();
